import { action, makeObservable, observable, runInAction } from 'mobx';

export class DrawerViewModel<TItem, TArgs = never> {
  public item?: TItem;
  public isOpen = false;
  public loading = false;
  protected args?: TArgs;

  public onClose?: () => void;
  public onOpen?: (item: TItem, args?: TArgs) => void;

  close() {
    this.isOpen = false;
    setTimeout(() => {
      runInAction(() => {
        this.item = undefined;
        this.args = undefined;
      });
    }, 100);
    this.onClose?.();
  }

  open(item: TItem, args?: TArgs) {
    runInAction(() => {
      this.item = item;
      this.isOpen = true;
      this.args = args;
    });
    this.onOpen?.(item, args);
  }

  async openAsync(getItem: () => Promise<TItem>, args?: TArgs) {
    runInAction(() => {
      this.loading = true;
      this.isOpen = true;
      this.args = args;
    });
    const item = await getItem();
    this.onOpen?.(item, args);
    runInAction(() => {
      this.item = item;
      this.loading = false;
    });
  }
  constructor() {
    makeObservable(this, {
      item: observable,
      loading: observable,
      isOpen: observable,
      open: action,
      openAsync: action,
      close: action
    });
  }
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export class Event<THandler extends (...args: any[]) => any> {
  private handlers: Array<THandler> = [];

  public subscribe(handler: THandler): void {
    this.handlers.push(handler);
  }

  public unsubscribe(handler: THandler): void {
    this.handlers = this.handlers.filter((h) => h !== handler);
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  public async trigger(...args: any[]): Promise<void> {
    for (const handler of this.handlers.slice(0)) {
      await handler(...args);
    }
  }
}

export class DialogViewModel<TItem, TArgs = never, TConfirmDetails = TArgs> extends DrawerViewModel<
  TItem,
  TArgs
> {
  public readonly onConfirm = new Event<
    (item?: TItem, details?: TConfirmDetails | TArgs) => void
  >();

  public onCancel?: () => void;

  public confirm() {
    this.onConfirm.trigger(this.getConfirmedItems(), this.getConfirmDetails());
    this.close();
  }

  public cancel() {
    this.onCancel?.();
    this.close();
  }

  protected getConfirmDetails(): TConfirmDetails | TArgs | undefined {
    return this.args;
  }

  protected getConfirmedItems() {
    return this.item;
  }

  constructor() {
    super();
    makeObservable(this, {
      confirm: action,
      cancel: action
    });
  }
}
export const useConfirmDialog = <TItem,>() => new DialogViewModel<TItem>();
export const useDrawer = <TItem,>() => new DrawerViewModel<TItem>();
